import {isFeatureEnabled} from '@github-ui/feature-flags'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepositoryMilestonesInternal} from '@github-ui/repository-milestone/RepositoryMilestones'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {getRepoIssuesSidebarPanes} from '../components/sidebar/get-repo-issues-sidebar-panes'
import {useQueryContext} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {IssueIndexPage, RepositoryMilestoneNewPage, RepositoryMilestonePage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {RepositoryMilestoneIndexPageContentInternal$key} from './__generated__/RepositoryMilestoneIndexPageContentInternal.graphql'
import type {RepositoryMilestoneIndexPageQuery} from './__generated__/RepositoryMilestoneIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneIndexPageQuery(
    $name: String!
    $owner: String!
    $state: MilestoneState!
    $orderField: MilestoneOrderField = CREATED_AT
    $orderDirection: OrderDirection = DESC
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneIndexPageContentInternal
        @arguments(state: $state, orderField: $orderField, orderDirection: $orderDirection)
    }
  }
`

export const RepositoryMilestoneIndexPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneIndexPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Index">
      <RepositoryMilestoneIndexContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneIndexContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneIndexPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneIndexPageQuery>(PageQuery, pageQueryRef)

  useEffect(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
      RepositoryMilestoneNewPage.preload()
      // When issue_pinned_views is on, the sidebar hook preloads IssueIndexPage.
      // Remove this line when the flag is cleaned up.
      if (!isFeatureEnabled('issue_pinned_views')) {
        IssueIndexPage.preload()
      }
    })
  }, [])

  const {setCurrentViewId} = useQueryContext()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneIndexPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneIndexPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneIndexPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneIndexPageContentInternal on Repository
      @argumentDefinitions(
        state: {type: "MilestoneState!"}
        orderField: {type: "MilestoneOrderField", defaultValue: CREATED_AT}
        orderDirection: {type: "OrderDirection", defaultValue: DESC}
      ) {
        ...RepositoryMilestonesInternal
          @arguments(state: $state, orderField: $orderField, orderDirection: $orderDirection)
      }
    `,
    repository,
  )

  const sidebarPanes = getRepoIssuesSidebarPanes()
  const issuePinnedViewsEnabled = isFeatureEnabled('issue_pinned_views')
  if (issuePinnedViewsEnabled) {
    return (
      <RepositoryMilestonesInternal
        repository={data}
        leftPane={sidebarPanes.leftPane}
        collapsibleLeftPane={sidebarPanes.collapsibleLeftPane}
      />
    )
  }

  return <RepositoryMilestonesInternal repository={data} />
}
